<template>
    <div class="regulation-related-substances">
        <div v-if="showHeader" class="regulation-related-substances__heading">
            <div>
                <div class="d-flex align-items-center flex-wrap">
                    <h6 class="mb-0">
                        {{ translate('Component(ResearchReviewProcess):related-substances', 'Related substances') }}
                    </h6>
                    <BBadge variant="light-secondary" class="ml-50">
                        {{ items.length }}
                    </BBadge>
                </div>
                <small class="text-less-muted d-block mt-25">
                    {{ translate(
                        'Component(ResearchReviewProcess):related-substances-description',
                        'Chemity substances associated with this regulation through the extracted document data.',
                    ) }}
                </small>
            </div>
            <BButton
                type="button"
                variant="flat-secondary"
                size="sm"
                class="btn-icon regulation-related-substances__toggle"
                :aria-expanded="String(!isCollapsed)"
                :aria-label="toggleLabel"
                :title="toggleLabel"
                @click="isCollapsed = !isCollapsed"
            >
                <FeatherIcon
                    :icon="isCollapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'"
                    size="18"
                />
            </BButton>
        </div>

        <BCollapse :visible="showHeader ? !isCollapsed : true">
            <div class="regulation-related-substances__list">
                <div
                    v-for="item in items"
                    :key="item.rowKey"
                    class="regulation-related-substances__row"
                >
                    <div class="min-width-0">
                        <strong class="d-block text-truncate">
                            {{ item.substance.name || translate('Component(ResearchReviewProcess):unnamed-substance', 'Unnamed substance') }}
                        </strong>
                        <div class="regulation-related-substances__identifiers mt-25">
                            <BBadge v-if="item.substance.casNumber" variant="light-secondary">
                                CAS: {{ item.substance.casNumber }}
                            </BBadge>
                            <BBadge v-if="item.substance.ecNumber" variant="light-secondary">
                                EC: {{ item.substance.ecNumber }}
                            </BBadge>
                        </div>
                    </div>
                    <span class="text-less-muted regulation-related-substances__concentration">
                        {{ concentrationLabel(item.substance.concentration) }}
                    </span>
                </div>
            </div>
        </BCollapse>
    </div>
</template>

<script>
import { BBadge, BButton, BCollapse } from 'bootstrap-vue'

export default {
    name: 'RegulationRelatedSubstances',
    components: {
        BBadge,
        BButton,
        BCollapse,
    },
    data() {
        return {
            isCollapsed: false,
        }
    },
    computed: {
        toggleLabel() {
            return this.isCollapsed
                ? this.translate('Component(CollapsibleCard):expand', 'Expand section')
                : this.translate('Component(CollapsibleCard):collapse', 'Collapse section')
        },
    },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        showHeader: {
            type: Boolean,
            default: true,
        },
    },
    methods: {
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        concentrationLabel(concentration) {
            if (!concentration
                || concentration.value === null
                || typeof concentration.value === 'undefined') {
                return this.translate(
                    'Component(ResearchReviewProcess):concentration-not-stated',
                    'Concentration not stated',
                )
            }

            return [
                concentration.operator,
                concentration.value,
                concentration.unit,
            ].filter(value => value !== null && typeof value !== 'undefined' && value !== '').join(' ')
        },
    },
}
</script>

<style lang="scss" scoped>
.regulation-related-substances__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: .75rem;
}

.regulation-related-substances__toggle {
    flex: 0 0 auto;
    padding: .25rem;
}

.regulation-related-substances__list {
    display: grid;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
}

.regulation-related-substances__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: .75rem;

    & + & {
        border-top: 1px solid #ebe9f1;
    }
}

.regulation-related-substances__identifiers {
    display: flex;
    flex-wrap: wrap;
    gap: .25rem;
}

.regulation-related-substances__concentration {
    flex: 0 0 auto;
    text-align: right;
}

.min-width-0 {
    min-width: 0;
}

@media (max-width: 575.98px) {
    .regulation-related-substances__row {
        align-items: flex-start;
        flex-direction: column;
        gap: .5rem;
    }

    .regulation-related-substances__concentration {
        text-align: left;
    }
}
</style>
